import type { EncounterPhotoApiItem } from 'features/Procedure/state/procedure-api.type'

type PhotoCategory = 'before' | 'after'

const isPhotoCategory = (value: string | null | undefined): value is PhotoCategory =>
  value === 'before' || value === 'after'

/**
 * Generates a filename for an encounter photo with proper sorting.
 * Format: {position}-{category}.{extension} (image name is removed, including 'photo' substring)
 * Where category is "A" for After and "B" for Before (A sorts before B alphabetically when mixed).
 * Legacy uploads may still use "0Before"/"1After" or "0"/"1" — parsers accept those too.
 *
 * @param category - Whether this is a 'before' or 'after' photo
 * @param existingPhotos - Array of existing photos to determine the next position
 * @param originalFileName - Original filename to extract name and extension
 * @returns Generated filename
 */
export const generateEncounterPhotoFileName = (
  category: PhotoCategory,
  existingPhotos: EncounterPhotoApiItem[],
  originalFileName: string,
): string => {
  const lastDotIndex = originalFileName.lastIndexOf('.')
  const extension =
    lastDotIndex > 0 ? originalFileName.substring(lastDotIndex + 1).toLowerCase() : 'jpg'

  const existingPositions = existingPhotos
    .map((photo) => {
      const photoCategory = isPhotoCategory(photo.category)
        ? photo.category
        : parseCategoryFromFileName(photo.filename)
      const photoPosition = parsePositionFromFileName(photo.filename)
      return { category: photoCategory, position: photoPosition }
    })
    .filter((p) => p.position > 0)

  const positionsByCategory = existingPositions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = []
    acc[p.category].push(p.position)
    return acc
  }, {} as Record<PhotoCategory, number[]>)

  const sameCategoryMax = Math.max(...(positionsByCategory[category] || [0]), 0)
  const oppositeCategory = category === 'before' ? 'after' : 'before'
  const oppositeCategoryMax = Math.max(...(positionsByCategory[oppositeCategory] || [0]), 0)

  const position =
    category === 'before'
      ? Math.max(sameCategoryMax + 1, oppositeCategoryMax + 1, 1)
      : sameCategoryMax === 0
      ? 1
      : Math.max(sameCategoryMax + 1, oppositeCategoryMax, 1)

  const categoryCode = category === 'before' ? 'B' : 'A'
  const paddedPosition = position.toString().padStart(2, '0')

  return `${paddedPosition}-${categoryCode}.${extension}`
}

/**
 * Parses the position from a photo filename.
 * Format: {position}-{category}.{extension} (legacy format: {imageName}-{position}-{category}.{extension})
 *
 * @param filename - The filename to parse
 * @returns The position number, or 0 if not found
 */
export const parsePositionFromFileName = (filename: string | null): number => {
  if (!filename) return 0

  const lastDotIndex = filename.lastIndexOf('.')
  const nameWithoutExt = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename

  const parts = nameWithoutExt.split('-')
  if (parts.length < 2) return 0

  const lastPart = parts[parts.length - 1]
  const isNewFormat =
    parts.length === 2 &&
    (lastPart === 'A' ||
      lastPart === 'B' ||
      lastPart === '0Before' ||
      lastPart === '1After' ||
      lastPart === '0' ||
      lastPart === '1')

  if (isNewFormat) {
    const position = parseInt(parts[0], 10)
    return isNaN(position) ? 0 : position
  }

  const positionStr = parts[parts.length - 2]
  const position = parseInt(positionStr, 10)

  return isNaN(position) ? 0 : position
}

/**
 * Parses the category from a photo filename.
 * Format: {position}-{category}.{extension} (legacy format: {imageName}-{position}-{category}.{extension})
 * Where category is "A" for After and "B" for Before (legacy: "0Before"/"1After", "0"/"1").
 *
 * @param filename - The filename to parse
 * @returns 'before' if before-side (B, 0Before, 0), 'after' if after-side (A, 1After, 1)
 */
export const parseCategoryFromFileName = (filename: string | null): PhotoCategory => {
  if (!filename) return 'before'

  const lastDotIndex = filename.lastIndexOf('.')
  const nameWithoutExt = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename

  const lastHyphenIndex = nameWithoutExt.lastIndexOf('-')
  if (lastHyphenIndex === -1) return 'before'

  const categoryCode = nameWithoutExt.substring(lastHyphenIndex + 1)

  if (
    categoryCode === 'A' ||
    categoryCode === '1After' ||
    categoryCode === '1'
  ) {
    return 'after'
  }
  return 'before'
}

/**
 * Updates the position segment in a photo filename.
 * Format: {position}-{category}.{extension} (legacy format: {imageName}-{position}-{category}.{extension})
 *
 * @param filename - The original filename
 * @param newPosition - The new position number (1-based)
 * @returns The filename with updated position, or original filename if parsing fails
 */
export const updatePositionInFileName = (filename: string | null, newPosition: number): string => {
  if (!filename) return filename || ''

  const lastDotIndex = filename.lastIndexOf('.')
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : ''
  const nameWithoutExt = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename

  const parts = nameWithoutExt.split('-')
  if (parts.length < 2) return filename

  const categoryCode = parts[parts.length - 1]
  const paddedPosition = newPosition.toString().padStart(2, '0')

  const isNewFormat =
    parts.length === 2 &&
    (categoryCode === 'A' ||
      categoryCode === 'B' ||
      categoryCode === '0Before' ||
      categoryCode === '1After' ||
      categoryCode === '0' ||
      categoryCode === '1')

  if (isNewFormat) {
    return `${paddedPosition}-${categoryCode}${extension}`
  }

  const otherParts = parts.slice(0, parts.length - 2)
  return `${otherParts.join('-')}-${paddedPosition}-${categoryCode}${extension}`
}
